import { Box, ChevronDown, ChevronRight, Flag, House, LayoutGrid, type LucideIcon, PanelLeftClose, PanelLeftOpen, Pentagon, Search, Spline, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { describe, STATUS_LABEL, type PlanKind, type PlanObject } from "@mcp/plan";
import { controller } from "../app/controller.ts";
import { select, useApp } from "../state/store.ts";
import { Checklist } from "./onboarding/Checklist.tsx";

const GROUPS: { kind: PlanKind; title: string; icon: LucideIcon }[] = [
  { kind: "building", title: "Buildings", icon: House },
  { kind: "area", title: "Areas", icon: Pentagon },
  { kind: "path", title: "Paths", icon: Spline },
  { kind: "marker", title: "Markers", icon: Flag },
];

/** The plan, on the left of the window: every object in this dimension, grouped. Folds into a rail (Ctrl B). */
export function PlansPanel() {
  const collapsed = useApp((s) => s.sideCollapsed);
  const set = useApp((s) => s.set);
  const narrow = useMedia("(max-width: 900px)");
  const [drawer, setDrawer] = useState(false);
  // narrow windows: always the rail; the list opens over the map as a drawer
  if (narrow) return <>
    <PlansRail onOpen={() => setDrawer(true)} />
    {drawer && <PlansList drawer onClose={() => setDrawer(false)} />}
  </>;
  return collapsed ? <PlansRail onOpen={() => set({ sideCollapsed: false })} /> : <PlansList onClose={() => set({ sideCollapsed: true })} />;
}

function useMedia(query: string) {
  const [match, setMatch] = useState(() => typeof matchMedia === "function" && matchMedia(query).matches);
  useEffect(() => {
    if (typeof matchMedia !== "function") return;
    const m = matchMedia(query);
    const on = () => setMatch(m.matches);
    on();
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, [query]);
  return match;
}

function usePlanList(q = "") {
  const plans = useApp((s) => s.plans);
  const dim = useApp((s) => s.dimension);
  return useMemo(() => {
    const t = q.trim().toLowerCase();
    return plans.filter((p) => p.dimension === dim && (!t || p.name.toLowerCase().includes(t) || p.notes.toLowerCase().includes(t)));
  }, [plans, dim, q]);
}

function PlansList({ drawer = false, onClose }: { drawer?: boolean; onClose: () => void }) {
  const selectedIds = useApp((s) => s.selectedIds);
  const [q, setQ] = useState("");
  const all = usePlanList();
  const list = usePlanList(q);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  // objects made together by a layout / template are listed as one collapsible group
  const groups = useMemo(() => {
    const m = new Map<string, PlanObject[]>();
    for (const p of list) if (p.group) m.set(p.group, [...(m.get(p.group) ?? []), p]);
    return [...m];
  }, [list]);

  const item = (p: PlanObject, nested: boolean) => (
    <button key={p.id} className={`plan-item${nested ? " nested" : ""}${selectedIds.includes(p.id) ? " sel" : ""}`} aria-current={selectedIds.includes(p.id) || undefined} title="Click to zoom to it · Shift/Ctrl+click to add to the selection" onClick={(e) => {
      if (e.shiftKey || e.ctrlKey || e.metaKey) select(selectedIds.includes(p.id) ? selectedIds.filter((x) => x !== p.id) : [...selectedIds, p.id]);
      else controller.focus(p);
    }}>
      {p.schematic
        ? <span className="swatch schem" style={{ color: p.color }} aria-hidden="true"><Box size={12} /></span>
        : <i className="swatch" style={{ background: p.color }} />}
      <span className="grow">
        <span className="name">{p.name}</span>
        <span className="muted tiny">{describe(p)}</span>
      </span>
      <span className={`status s-${p.status}`}><i className={`status-dot s-${p.status}`} />{STATUS_LABEL[p.status]}</span>
    </button>
  );

  return (
    <aside className={`side panel plans${drawer ? " drawer" : ""}`} aria-label="Plan">
      <div className="side-head">
        <span className="side-title">Plan</span>
        <span className="side-count tabular">{all.length} {all.length === 1 ? "object" : "objects"}</span>
        <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose} aria-expanded="true" aria-label={drawer ? "Close the plan list" : "Hide plan list (Ctrl B)"} title={drawer ? "Close" : "Hide plan · Ctrl B"}><PanelLeftClose size={15} aria-hidden="true" /></button>
      </div>
      <label className="plan-search">
        <Search size={14} aria-hidden="true" />
        <input placeholder="Search plan…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Escape" && q) { e.stopPropagation(); setQ(""); } }} aria-label="Search plan" />
        {q ? <button className="btn btn-ghost btn-icon clear" onClick={() => setQ("")} aria-label="Clear search" title="Clear"><X size={13} aria-hidden="true" /></button> : <kbd>Ctrl F</kbd>}
      </label>
      <div className="side-scroll">
        {all.length === 0 && (
          <div className="side-empty">
            <span className="side-empty-art" aria-hidden="true"><Spline size={18} /></span>
            <b>Nothing planned yet</b>
            <p>Press <kbd>2</kbd> and click on the map to draw a path, or pick another tool on the left (<kbd>3</kbd>–<kbd>5</kbd>). <kbd>?</kbd> shows every shortcut.</p>
          </div>
        )}
        {all.length > 0 && list.length === 0 && <p className="side-none">Nothing matches “{q}”.</p>}
        {groups.length > 0 && (
          <div className="group-list">
            <div className="group-title"><span>Groups</span><span className="tabular">{groups.length}</span></div>
            {groups.map(([gid, members]) => (
              <div key={gid} className="obj-group">
                <button className={`plan-item${members.every((m) => selectedIds.includes(m.id)) ? " sel" : ""}`} onClick={() => controller.selectGroup(gid)} title="Select the whole group (drag to move it, Delete to remove it)">
                  <span className="group-toggle" role="button" aria-label={openGroups[gid] ? "Fold the group" : "Show what's in the group"} onClick={(e) => { e.stopPropagation(); setOpenGroups((o) => ({ ...o, [gid]: !o[gid] })); }}>{openGroups[gid] ? <ChevronDown size={14} aria-hidden="true" /> : <ChevronRight size={14} aria-hidden="true" />}</span>
                  <span className="grow">
                    <span className="name">{members[0].groupName ?? "Group"}</span>
                    <span className="muted tiny">{members.length} objects · {members.filter((m) => m.kind === "building").length} buildings</span>
                  </span>
                  <LayoutGrid className="group-icon" size={13} aria-hidden="true" />
                </button>
                {openGroups[gid] && members.map((p) => item(p, true))}
              </div>
            ))}
          </div>
        )}
        {GROUPS.map((g) => {
          const items = list.filter((p) => p.kind === g.kind && !p.group);
          if (!items.length) return null;
          return (
            <div key={g.kind} className="group-list">
              <div className="group-title"><span>{g.title}</span><span className="tabular">{items.length}</span></div>
              {items.map((p) => item(p, false))}
            </div>
          );
        })}
      </div>
      <div className="side-foot">
        <Checklist />
      </div>
    </aside>
  );
}

/** The folded plan list: one button per kind with its count; any of them opens the list again. */
function PlansRail({ onOpen: open }: { onOpen: () => void }) {
  const list = usePlanList();
  return (
    <aside className="side rail" aria-label="Plan, folded">
      <button className="btn btn-ghost btn-icon rail-open" onClick={open} aria-expanded="false" aria-label="Show plan list (Ctrl B)" title="Show plan · Ctrl B"><PanelLeftOpen size={16} aria-hidden="true" /></button>
      <span className="rail-sep" aria-hidden="true" />
      {GROUPS.map((g) => {
        const n = list.filter((p) => p.kind === g.kind).length;
        return (
          <button key={g.kind} className="rail-btn" onClick={open} aria-label={`${g.title}: ${n}. Show plan list`} title={`${g.title} · ${n}`}>
            <g.icon size={17} aria-hidden="true" />
            <span className="tabular">{n}</span>
          </button>
        );
      })}
      <span className="spacer" />
      <div className="rail-foot"><Checklist compact /></div>
    </aside>
  );
}
